// Form rules of "Nueva gestión" (design RolloNewSelect, S3 and S9). The option lists are the codes the server
// validates, not business data: everything about the client comes from the search and contexto endpoints.
import { CANAL_LABELS, formatRollos, todayIso } from "../rolloFormat";

export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;

export const CANAL_OPTIONS = Object.entries(CANAL_LABELS).map(([value, label]) => ({ value, label }));

export const SIN_ROLLOS_OPTIONS = [
  { value: "SI", label: "Sí" },
  { value: "NO", label: "No" },
];

// Same limits as RolloGestionService (cantidad 1–50, text lengths of CRM_RolloGestion).
export const LIMITS = {
  cantidadMin: 1,
  cantidadMax: 50,
  direccion: 300,
  contactoNombre: 150,
  contactoTelefono: 50,
  observacion: 1000,
};

// Form keys in the order they appear on screen, with the id suffix of their control: the first invalid one
// gets the focus. canal points at its first radio.
export const FIELD_IDS = {
  comercio: "comercio",
  terminal: "terminal",
  cantidad: "cantidad",
  canal: "canal",
  direccion: "direccion",
  contactoNombre: "contacto-nombre",
  contactoTelefono: "contacto-telefono",
  fecha: "fecha",
  observacion: "observacion",
};

export const fieldId = (key) => `crm-rollo-nueva-${FIELD_IDS[key]}`;
export const errorId = (key) => `${fieldId(key)}-error`;

// The search endpoint compares digits: a CUIT typed with dashes or dots is sent without them.
export const normalizeClienteSearch = (value) => {
  const text = String(value ?? "").trim();
  return /^[\d\s.-]+$/.test(text) ? text.replace(/[\s.-]/g, "") : text;
};

// ---- Comercio of the gestión (S3a). The client group of step 1 holds one entry per alta; the gestión belongs to
// one comercio, so step 2 asks for it when the client has several and requests the contexto of one of its altas.
const textOf = (value) => String(value ?? "").trim();
const isAlta = (estado) => textOf(estado).toUpperCase() === "ALTA";
export const plural = (n, singular, pluralLabel) => `${n} ${n === 1 ? singular : pluralLabel}`;

// Trimmed text of a client search field; the search answers "-" for empty texts.
export const cleanClienteText = (value) => (textOf(value) === "-" ? "" : textOf(value));

const compareComercios = (a, b) => {
  if (!a.numero || !b.numero) return a.numero ? -1 : b.numero ? 1 : 0;
  const left = Number(a.numero);
  const right = Number(b.numero);
  return Number.isFinite(left) && Number.isFinite(right) && left !== right ? left - right : a.numero.localeCompare(b.numero);
};

// One option per comercio of the group (`agruparClientesPorGrupo`), pointing at an ALTA alta of that comercio
// (else its first one). Altas without comercio number share one option keyed by the chosen alta.
export const buildComercioOptions = (cliente) => {
  if (!cliente) return [];
  const entries = cliente.terminales?.length
    ? cliente.terminales
    : [{ clienteId: cliente.clienteId, terminal: cliente.nDeTerminal, comercio: cliente.nDeComercio, estado: cliente.estado }];

  const porComercio = entries.reduce((acc, entry) => {
    if (!entry?.clienteId) return acc;
    const numero = textOf(entry.comercio);
    if (!acc.has(numero)) acc.set(numero, []);
    acc.get(numero).push(entry);
    return acc;
  }, new Map());

  return Array.from(porComercio.entries())
    .map(([numero, items]) => {
      const alta = items.find((item) => isAlta(item.estado)) || items[0];
      return {
        key: numero || `cliente-${alta.clienteId}`,
        numero,
        clienteId: alta.clienteId,
        terminales: new Set(items.map((item) => textOf(item.terminal)).filter(Boolean)).size,
      };
    })
    .sort(compareComercios);
};

// "Comercio 5001 · 2 terminales" / "Sin número de comercio · 1 terminal".
export const comercioOptionLabel = (comercio) =>
  [
    comercio.numero ? `Comercio ${comercio.numero}` : "Sin número de comercio",
    comercio.terminales ? plural(comercio.terminales, "terminal", "terminales") : "",
  ]
    .filter(Boolean)
    .join(" · ");

// The contexto lists every alta of the CUIT: the terminal select only offers the chosen comercio's ones.
export const terminalesDelComercio = (terminales, numeroComercio) =>
  (terminales || []).filter((terminal) => textOf(terminal?.numeroComercio) === textOf(numeroComercio));

export const validateComercio = (comercios, comercioKey) =>
  comercios.length > 1 && !comercios.some((comercio) => comercio.key === comercioKey) ? { comercio: "Elegí el comercio" } : {};

// Header of step 2 before the contexto answers, from the group of step 1.
export const buildClienteResumen = (cliente) => ({
  nombre: cleanClienteText(cliente?.nombreDeFantasia) || cleanClienteText(cliente?.razonSocial),
  razonSocial: cleanClienteText(cliente?.razonSocial),
  cuit: cleanClienteText(cliente?.cuit),
  legajo: cleanClienteText(cliente?.legajo),
});

// Prefilled from the contexto (dirección and contacto stay editable). The terminal starts empty: it is optional
// in the whole process (S3), even when the client has a single one.
export const buildInitialForm = (contexto) => ({
  terminal: "",
  cantidad: "",
  canal: "",
  sinRollos: "NO",
  direccion: contexto?.prefill?.direccionEntrega || "",
  contactoNombre: contexto?.prefill?.contactoNombre || "",
  contactoTelefono: contexto?.prefill?.contactoTelefono || "",
  fecha: "",
  observacion: "",
});

// Another comercio was chosen: its terminal and its prefill replace the previous ones; what the asesor typed about
// the pedido itself (cantidad, canal, fecha, observación) stays.
export const applyComercioPrefill = (form, contexto) => {
  const initial = buildInitialForm(contexto);
  if (!form) return initial;
  return {
    ...form,
    terminal: initial.terminal,
    direccion: initial.direccion,
    contactoNombre: initial.contactoNombre,
    contactoTelefono: initial.contactoTelefono,
  };
};

const tooLong =(value, max) => String(value ?? "").trim().length > max;

export const validateGestionForm = (form, today = todayIso()) => {
  const errors = {};
  const cantidad = String(form.cantidad ?? "").trim();
  const numero = Number(cantidad);

  if (!cantidad) {
    errors.cantidad = "Ingresá la cantidad de rollos";
  } else if (!Number.isInteger(numero) || numero < LIMITS.cantidadMin || numero > LIMITS.cantidadMax) {
    errors.cantidad = `Ingresá un número entero entre ${LIMITS.cantidadMin} y ${LIMITS.cantidadMax}`;
  }

  if (!form.canal) errors.canal = "Elegí por dónde pidió";
  if (tooLong(form.direccion, LIMITS.direccion)) {
    errors.direccion = `Acortá la dirección a ${LIMITS.direccion} caracteres como máximo`;
  }
  if (tooLong(form.contactoNombre, LIMITS.contactoNombre)) {
    errors.contactoNombre = `Acortá el nombre a ${LIMITS.contactoNombre} caracteres como máximo`;
  }
  if (tooLong(form.contactoTelefono, LIMITS.contactoTelefono)) {
    errors.contactoTelefono = `Acortá el teléfono a ${LIMITS.contactoTelefono} caracteres como máximo`;
  }
  // ISO dates (YYYY-MM-DD) compare as text.
  if (form.fecha && today && form.fecha < today) {
    errors.fecha = "La fecha estimada no puede ser anterior a hoy";
  }
  if (tooLong(form.observacion, LIMITS.observacion)) {
    errors.observacion = `Acortá la observación a ${LIMITS.observacion} caracteres como máximo`;
  }

  return errors;
};

export const firstErrorKey = (errors) => Object.keys(FIELD_IDS).find((key) => errors[key]) || null;

const textOrNull = (value) => {
  const text = String(value ?? "").trim();
  return text || null;
};

// Body of POST gestiones without the correlationId (the caller adds it, see createCorrelationId).
export const buildCrearPayload = (clienteId, form) => ({
  clienteId,
  numeroTerminal: form.terminal || null,
  canalPedido: form.canal,
  cantidadSolicitada: Number(form.cantidad),
  sinRollos: form.sinRollos === "SI",
  direccionEntrega: textOrNull(form.direccion),
  contactoNombre: textOrNull(form.contactoNombre),
  contactoTelefono: textOrNull(form.contactoTelefono),
  fechaEstimadaEntrega: form.fecha || null,
  observacion: textOrNull(form.observacion),
});

// The server binds CorrelationId as a Guid, so the fallback also produces a version 4 UUID.
export const createCorrelationId = () => {
  const cryptoApi = typeof crypto !== "undefined" ? crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  const randomNibble = () =>
    cryptoApi && typeof cryptoApi.getRandomValues === "function"
      ? cryptoApi.getRandomValues(new Uint8Array(1))[0] % 16
      : Math.floor(Math.random() * 16);

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const nibble = randomNibble();
    return (char === "x" ? nibble : 8 + (nibble % 4)).toString(16);
  });
};

// Toast detail: comercio, cantidad and prioridad (Alta when it ran out of rollos, S9).
export const buildToastText = (cliente, payload) =>
  [
    cliente?.nombre || cliente?.razonSocial || "",
    formatRollos(payload.cantidadSolicitada),
    `prioridad ${payload.sinRollos ? "Alta" : "Media"}`,
  ]
    .filter(Boolean)
    .join(" · ");

export const describeCreateError = (error) => ({
  title: "No pudimos crear la gestión",
  text: error?.status
    ? error.message || "Probá de nuevo en unos segundos."
    : "No pudimos conectar con el servidor. Probá de nuevo: el pedido no se va a duplicar.",
});

const capitalize = (value) => {
  const text = String(value ?? "").trim().toLowerCase();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

// "69002 · Comercio 5001 · Baja": the estado is only written when the terminal is not ALTA.
export const terminalOptionLabel = (terminal) =>
  [
    terminal.numeroTerminal,
    terminal.numeroComercio ? `Comercio ${terminal.numeroComercio}` : "",
    terminal.estado && String(terminal.estado).trim().toUpperCase() !== "ALTA" ? capitalize(terminal.estado) : "",
  ]
    .filter(Boolean)
    .join(" · ");
